import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Globe, Bot, Zap, TrendingUp, Settings, MapPin, Users, Clock, Wrench, Headphones as HeadphonesIcon, ArrowRight, ChevronRight, HelpCircle, CheckCircle, Star, MessageCircle, Phone } from 'lucide-react';
import { trackButtonClick } from '../lib/analytics';
import NewsletterFormSection from '../components/NewsletterFormSection';
import ReviewCarousel from '../components/ReviewCarousel';
import { RatingBadgesRow, RatingBadgesStack } from '../components/RatingBadges';

const HeroSection = () => {
  const navigate = useNavigate();

  const handleStartProject = () => {
    trackButtonClick('Start Your Project', 'Hero');
    navigate('/contact');
  };

  const handleAudit = () => {
    trackButtonClick('Free Website Audit', 'Hero');
    window.open('https://aiwebsolutions.tech', '_blank');
  };

  const handleWhatsApp = () => {
    trackButtonClick('WhatsApp', 'Hero');
    window.open('https://wa.me/4407624474095', '_blank');
  };

  return (
    <section className="min-h-screen flex items-center relative overflow-hidden">
      <div className="lg:hidden h-full w-full flex flex-col justify-center px-5 py-24">
        <div className="flex items-center justify-between gap-6 mb-8">
          <h1 className="text-2xl sm:text-3xl font-semibold text-white leading-tight tracking-tight text-left flex-1">
            <span className="page-title text-xl sm:text-2xl">Digital Systems</span>
            <span className="block text-gradient mt-2 text-2xl sm:text-3xl">Built Properly.</span>
          </h1>
          <div className="relative w-24 h-24 sm:w-28 sm:h-28 flex-shrink-0">
            <img
              src="/untitled_design_(24).png"
              alt="AI Web Solutions logo"
              className="rounded-2xl object-cover w-full h-full"
            />
          </div>
        </div>

        <p className="text-sm sm:text-base text-gray-400 leading-relaxed mb-10">
          We design and build modern websites, applications, and automation systems for Isle of Man and UK businesses — fast, reliable, and built to scale.
        </p>

        <div className="flex flex-col items-center gap-4 w-full">
          <button onClick={handleStartProject} className="btn-primary w-full max-w-sm">
            Start Your Project
            <ArrowRight className="ml-2 h-4 w-4 inline" />
          </button>
          <button onClick={handleAudit} className="btn-secondary w-full max-w-sm">
            Free Website Audit
          </button>
          <button
            onClick={handleWhatsApp}
            className="inline-flex items-center justify-center text-sm text-gray-500 hover:text-gray-300 transition-colors w-full max-w-sm py-3"
          >
            <MessageCircle className="h-4 w-4 mr-2" />
            Message us on WhatsApp
          </button>
          <RatingBadgesStack />
        </div>
      </div>

      <div className="hidden lg:block relative z-10 max-w-6xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <div className="text-left">
            <h1 className="text-5xl xl:text-6xl font-semibold text-white leading-[1.1] tracking-tight">
              <span className="page-title block mb-3">Digital Systems</span>
              <span className="block text-gradient">Built Properly.</span>
            </h1>

            <p className="mt-8 text-lg text-gray-400 leading-relaxed max-w-lg">
              We design and build modern websites, applications, and automation systems for Isle of Man and UK businesses — fast, reliable, and built to scale.
            </p>

            <div className="mt-10 flex items-center gap-3">
              <button onClick={handleAudit} className="btn-secondary text-sm px-5 py-2 whitespace-nowrap inline-flex items-center justify-center">
                Free Website Audit
              </button>
              <button onClick={handleStartProject} className="btn-primary text-sm px-5 py-2 whitespace-nowrap inline-flex items-center justify-center">
                Start Your Project
                <ArrowRight className="ml-2 h-4 w-4" />
              </button>
            </div>

            <button
              onClick={handleWhatsApp}
              className="mt-4 inline-flex items-center text-sm text-gray-500 hover:text-gray-300 transition-colors"
            >
              <MessageCircle className="h-4 w-4 mr-2" />
              Message us on WhatsApp
            </button>
          </div>

          <div className="relative flex flex-col items-center justify-center">
            <div className="relative w-full max-w-md">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent rounded-3xl blur-3xl scale-110" />
              <img
                src="/untitled_design_(24).png"
                alt="Modern technology workspace"
                className="relative rounded-2xl object-cover w-full aspect-square"
              />
            </div>
            <div className="mt-4">
              <RatingBadgesRow />
            </div>
          </div>
        </div>
      </div>

    </section>
  );
};

const IntentRoutingSection = () => {
  const navigate = useNavigate();

  const intents = [
    {
      icon: Globe,
      title: 'Website or Online Presence',
      description: 'Modern, fast, conversion-focused websites',
      target: '/services#websites',
    },
    {
      icon: Bot,
      title: 'AI or Automation',
      description: 'Intelligent systems that work 24/7',
      target: '/services#automation',
    },
    {
      icon: TrendingUp,
      title: 'Marketing & Growth',
      description: 'SEO, content, and digital presence',
      target: '/services#marketing',
    },
    {
      icon: HelpCircle,
      title: 'Not sure yet — guide me',
      description: 'Let us help you figure it out',
      target: '/contact',
    },
  ];

  const handleCardClick = (target: string) => {
    trackButtonClick(`Intent: ${target}`, 'Intent Routing');
    navigate(target);
  };

  return (
    <section className="section-spacing">
      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-lg sm:text-xl text-gray-400 font-light">What are you looking to build?</p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {intents.map((intent, index) => (
            <button
              key={index}
              onClick={() => handleCardClick(intent.target)}
              className="group p-8 rounded-2xl text-left bg-white/[0.02] hover:bg-white/[0.04] border border-white/[0.04] hover:border-white/[0.08] transition-all duration-500"
            >
              <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/15 transition-colors duration-300">
                <intent.icon className="h-5 w-5 text-primary/80" />
              </div>
              <h3 className="text-white font-medium mb-2 text-[15px]">{intent.title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{intent.description}</p>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

const TrustSignalBar = () => {
  const signals = [
    { icon: MapPin, text: 'Isle of Man based' },
    { icon: Users, text: 'UK-wide clients' },
    { icon: Clock, text: 'Fast turnaround' },
    { icon: Wrench, text: 'Custom-built systems' },
    { icon: HeadphonesIcon, text: 'Ongoing support' },
  ];

  return (
    <section className="py-10 lg:py-12">
      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="flex flex-wrap justify-center items-center gap-x-10 gap-y-4 lg:gap-x-16">
          {signals.map((signal, index) => (
            <div key={index} className="flex items-center text-gray-500 text-sm">
              <signal.icon className="h-4 w-4 mr-2.5 text-gray-600" />
              <span>{signal.text}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const WhatWeDoSection = () => {
  const navigate = useNavigate();

  const services = [
    {
      icon: Globe,
      title: 'Websites & Web Platforms',
      description: 'Fast, modern websites that look great and convert visitors into customers.',
    },
    {
      icon: Settings,
      title: 'Applications & SaaS',
      description: 'Custom web applications built to solve your specific business problems.',
    },
    {
      icon: Bot,
      title: 'AI & Automation Systems',
      description: 'Intelligent chatbots and automation that handle tasks 24/7.',
    },
    {
      icon: TrendingUp,
      title: 'Marketing, SEO & Growth',
      description: 'Get found online and turn traffic into real business results.',
    },
    {
      icon: HeadphonesIcon,
      title: 'Ongoing Support & Management',
      description: 'Keep everything running smoothly with dedicated support.',
    },
  ];

  return (
    <section className="section-spacing-lg">
      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="text-center mb-16 lg:mb-20">
          <h2 className="page-title text-2xl sm:text-3xl lg:text-4xl font-semibold text-white mb-5">Why Choose Our Web Services?</h2>
          <p className="text-gray-500 max-w-xl mx-auto leading-relaxed">
            From websites to AI automation, we build digital systems that actually work for your business.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
          {services.map((service, index) => (
            <div
              key={index}
              className="group"
            >
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-5">
                <service.icon className="h-5 w-5 text-primary/70" />
              </div>
              <h3 className="text-[17px] font-medium text-white mb-3">{service.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{service.description}</p>
            </div>
          ))}
        </div>

        <div className="text-center mt-16">
          <button
            onClick={() => {
              trackButtonClick('View All Services', 'What We Do');
              navigate('/services');
            }}
            className="text-sm text-gray-400 hover:text-white transition-colors inline-flex items-center"
          >
            View All Services
            <ChevronRight className="ml-1 h-4 w-4" />
          </button>
        </div>
      </div>
    </section>
  );
};

const WhyDifferentSection = () => {
  const differentiators = [
    {
      title: 'Speed without shortcuts',
      description: 'We deliver fast because we work efficiently, not because we cut corners.',
    },
    {
      title: 'Local accountability',
      description: 'Isle of Man based with real people you can actually talk to.',
    },
    {
      title: 'Systems thinking',
      description: 'We build integrated solutions, not just standalone websites.',
    },
    {
      title: 'Built to grow with you',
      description: 'Every system is designed to scale as your business expands.',
    },
  ];

  return (
    <section className="section-spacing-lg">
      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <div>
            <h2 className="page-title text-2xl sm:text-3xl lg:text-4xl font-semibold text-white mb-10">
              Key Benefits of Solutions
            </h2>

            <div className="space-y-8">
              {differentiators.map((item, index) => (
                <div key={index} className="flex items-start">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/15 flex items-center justify-center mt-1 mr-4">
                    <CheckCircle className="h-3 w-3 text-primary/80" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium mb-1.5">{item.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <p className="mt-12 text-gray-500 text-sm leading-relaxed border-l-2 border-white/10 pl-5">
              You don't need to understand the technology — that's our job. We explain everything clearly and handle the complexity for you.
            </p>
          </div>

          <div className="hidden lg:block relative">
            <div className="relative w-full aspect-[4/3]">
              <img
                src="/untitled_design_(25).png"
                alt="System architecture visualization"
                className="w-full h-full object-cover rounded-2xl opacity-60"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const ShowcasePreviewSection = () => {
  const navigate = useNavigate();

  const projects = [
    {
      type: 'E-commerce Website',
      problem: 'Outdated platform losing mobile customers',
      solution: 'Modern responsive storefront with streamlined checkout',
      outcome: 'Mobile conversions increased significantly',
    },
    {
      type: 'AI Chatbot',
      problem: 'Support team overwhelmed with repetitive queries',
      solution: '24/7 intelligent assistant handling common questions',
      outcome: 'Support workload reduced, faster response times',
    },
    {
      type: 'Business Website',
      problem: 'No online presence in competitive market',
      solution: 'Professional site with SEO and lead capture',
      outcome: 'Consistent organic traffic and enquiries',
    },
  ];

  return (
    <section className="section-spacing-lg">
      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="page-title text-2xl sm:text-3xl lg:text-4xl font-semibold text-white mb-4">Success Stories and Client Results</h2>
          <p className="text-gray-500">Real problems, real solutions, real results.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {projects.map((project, index) => (
            <div
              key={index}
              className="p-7 rounded-2xl bg-white/[0.02] border border-white/[0.04]"
            >
              <span className="inline-block px-3 py-1.5 text-xs font-medium text-primary/80 bg-primary/10 rounded-lg mb-6">
                {project.type}
              </span>
              <div className="space-y-5">
                <div>
                  <p className="text-[11px] text-gray-600 uppercase tracking-wider mb-1.5">Problem</p>
                  <p className="text-gray-400 text-sm leading-relaxed">{project.problem}</p>
                </div>
                <div>
                  <p className="text-[11px] text-gray-600 uppercase tracking-wider mb-1.5">Solution</p>
                  <p className="text-gray-400 text-sm leading-relaxed">{project.solution}</p>
                </div>
                <div>
                  <p className="text-[11px] text-gray-600 uppercase tracking-wider mb-1.5">Outcome</p>
                  <p className="text-white/90 text-sm font-medium">{project.outcome}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-14">
          <button
            onClick={() => {
              trackButtonClick('View Full Showcase', 'Showcase Preview');
              navigate('/showcase');
            }}
            className="text-sm text-gray-400 hover:text-white transition-colors inline-flex items-center"
          >
            View Full Showcase
            <ChevronRight className="ml-1 h-4 w-4" />
          </button>
        </div>
      </div>
    </section>
  );
};

const HowItWorksSection = () => {
  const steps = [
    {
      number: '01',
      title: 'Discovery & Planning',
      description: 'We learn about your business, goals, and requirements.',
    },
    {
      number: '02',
      title: 'Design & Structure',
      description: 'We create wireframes and designs for your approval.',
    },
    {
      number: '03',
      title: 'Build & Integration',
      description: 'We develop your solution with regular progress updates.',
    },
    {
      number: '04',
      title: 'Testing & Refinement',
      description: 'Thorough testing and adjustments based on your feedback.',
    },
    {
      number: '05',
      title: 'Launch & Support',
      description: 'Go live with confidence and ongoing support.',
    },
  ];

  return (
    <section className="section-spacing-lg">
      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="text-center mb-16 lg:mb-20">
          <h2 className="page-title text-2xl sm:text-3xl lg:text-4xl font-semibold text-white mb-4">How Our Web Process Works</h2>
          <p className="text-gray-500">No jargon. No surprises. Clear communication throughout.</p>
        </div>

        <div className="hidden lg:block relative">
          <div className="absolute top-7 left-[10%] right-[10%] h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
          <div className="grid grid-cols-5 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative text-center">
                <div className="w-14 h-14 mx-auto rounded-full bg-white/[0.03] border border-white/10 flex items-center justify-center mb-6 relative z-10">
                  <span className="text-gray-400 font-medium text-sm">{step.number}</span>
                </div>
                <h3 className="text-white font-medium mb-2.5 text-sm">{step.title}</h3>
                <p className="text-gray-500 text-xs leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:hidden space-y-8">
          {steps.map((step, index) => (
            <div key={index} className="flex items-start">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-white/[0.03] border border-white/10 flex items-center justify-center mr-5">
                <span className="text-gray-400 text-sm font-medium">{step.number}</span>
              </div>
              <div>
                <h3 className="text-white font-medium mb-1.5">{step.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const SocialProofSection = () => {
  const testimonials = [
    {
      quote: "They transformed our online presence completely. Professional, fast, and actually listened to what we needed.",
      author: 'Local Business Owner',
      role: 'Retail, Isle of Man',
    },
    {
      quote: "The AI chatbot they built handles most of our customer enquiries now. It's been a game-changer for our team.",
      author: 'Service Company Director',
      role: 'Professional Services, UK',
    },
    {
      quote: "Finally a web company that explains things clearly without all the tech jargon. Highly recommend.",
      author: 'Startup Founder',
      role: 'Tech Startup, UK',
    },
  ];

  return (
    <section className="section-spacing-lg">
      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="page-title text-2xl sm:text-3xl lg:text-4xl font-semibold text-white">What Clients Say</h2>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <div
              key={index}
              className="p-7"
            >
              <div className="flex mb-5 gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-3.5 w-3.5 text-primary/70 fill-primary/70" />
                ))}
              </div>
              <p className="text-gray-400 text-sm leading-relaxed mb-6">"{testimonial.quote}"</p>
              <div>
                <p className="text-white/90 font-medium text-sm">{testimonial.author}</p>
                <p className="text-gray-600 text-xs mt-0.5">{testimonial.role}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const FinalCTASection = () => {
  const navigate = useNavigate();
  const sectionRef = useRef<HTMLElement>(null);
  const [ctaText, setCtaText] = useState('Start Your Project');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setCtaText("Let's Talk");
          }
        });
      },
      { threshold: 0.5 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleStartProject = () => {
    trackButtonClick('Start Your Project', 'Final CTA');
    navigate('/contact');
  };

  const handleBookCall = () => {
    trackButtonClick('Book a Call', 'Final CTA');
    window.open('https://wa.me/4407624474095', '_blank');
  };

  return (
    <section ref={sectionRef} className="section-spacing-lg">
      <div className="max-w-2xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
        <h2 className="page-title text-2xl sm:text-3xl lg:text-4xl font-semibold text-white mb-6">
          Get Started with Web Today
        </h2>
        <p className="text-gray-500 mb-10 max-w-lg mx-auto leading-relaxed">
          Whether you need a website, automation system, or aren't quite sure — let's have a conversation about what would work best for your business.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <button onClick={handleStartProject} className="btn-primary">
            {ctaText}
            <ArrowRight className="ml-2 h-4 w-4 inline" />
          </button>
          <button onClick={handleBookCall} className="btn-secondary">
            <Phone className="mr-2 h-4 w-4 inline" />
            Book a Call
          </button>
        </div>
      </div>
    </section>
  );
};

const HomePage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('newsletter') === 'confirmed') {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      toast.success("You're on the list \u2014 subscription confirmed.", { duration: 6000 });
      params.delete('newsletter');
      const nextSearch = params.toString();
      navigate(
        { pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' },
        { replace: true }
      );
    }
  }, [location.pathname, location.search, navigate]);

  return (
    <>
      <HeroSection />
      <ReviewCarousel />
      <IntentRoutingSection />
      <TrustSignalBar />
      <WhyDifferentSection />
      <WhatWeDoSection />
      <ShowcasePreviewSection />
      <HowItWorksSection />
      <SocialProofSection />
      <NewsletterFormSection />
      <FinalCTASection />
    </>
  );
};

export default HomePage;
